import React from "react";
import useScrollMotion from "../hooks/useScrollMotion";
import { PALMS } from "../mock";

/**
 * Scattered sage-green palm watermarks placed at intervals down the page.
 * Lives BELOW the hero — placed absolutely on the App container so they
 * scroll naturally with content. Each palm reacts to scroll: gust on
 * velocity, parallax drift, and ambient sway.
 */

// Hand-tuned positions through the page. `top` is in viewport-heights from
// the very top of the document, so 100vh is right at the start of the
// content under the cinematic hero.
const SCATTER = [
  { side: "left",  offset: "-2vw",  top: "108vh", size: 110, rot: -6, parallax: 0.06, phase: -0.4, flip: false },
  { side: "right", offset: "-1vw",  top: "118vh", size: 130, rot: 5,  parallax: 0.08, phase: -1.6, flip: true  },
  { side: "left",  offset: "8vw",   top: "150vh", size: 86,  rot: 8,  parallax: 0.05, phase: -2.7, flip: true  },
  { side: "right", offset: "7vw",   top: "170vh", size: 100, rot: -4, parallax: 0.07, phase: -0.9, flip: false },
  { side: "left",  offset: "-3vw",  top: "200vh", size: 140, rot: -7, parallax: 0.06, phase: -3.1, flip: false },
  { side: "right", offset: "-2vw",  top: "228vh", size: 120, rot: 6,  parallax: 0.07, phase: -1.2, flip: true  },
  { side: "left",  offset: "10vw",  top: "260vh", size: 78,  rot: 4,  parallax: 0.05, phase: -2.4, flip: false },
  { side: "right", offset: "11vw",  top: "285vh", size: 92,  rot: -5, parallax: 0.06, phase: -0.6, flip: true  },
  { side: "left",  offset: "-1vw",  top: "320vh", size: 130, rot: 7,  parallax: 0.07, phase: -3.6, flip: false },
  { side: "right", offset: "-2vw",  top: "355vh", size: 110, rot: -3, parallax: 0.06, phase: -1.9, flip: true  },
  { side: "left",  offset: "8vw",   top: "395vh", size: 84,  rot: 9,  parallax: 0.05, phase: -2.2, flip: true  },
  { side: "right", offset: "9vw",   top: "430vh", size: 96,  rot: -6, parallax: 0.06, phase: -0.7, flip: false },
  { side: "left",  offset: "-2vw",  top: "475vh", size: 120, rot: 5,  parallax: 0.07, phase: -2.0, flip: false },
  { side: "right", offset: "-1vw",  top: "520vh", size: 140, rot: -4, parallax: 0.06, phase: -1.0, flip: true  },
];

const BackgroundPalms = () => {
  const { y, velocity } = useScrollMotion();
  const baseGust = Math.max(-12, Math.min(12, velocity * 0.7));

  return (
    <>
      {SCATTER.map((p, i) => {
        const driftAngle = Math.sin(y / 140 + p.phase) * 14;
        const angle = p.rot + driftAngle + baseGust * (i % 2 === 0 ? 1 : -0.7);
        const yShift = -y * p.parallax * 1.2;
        const xShift = Math.sin(y / 320 + p.phase) * 10 * (i % 2 === 0 ? 1 : -1);
        const positionStyle =
          p.side === "left" ? { left: p.offset } : { right: p.offset };
        return (
          <div
            key={i}
            aria-hidden="true"
            className="absolute pointer-events-none"
            style={{
              ...positionStyle,
              top: p.top,
              width: p.size,
              zIndex: 1,
              transform: `translate3d(${xShift}px, ${yShift}px, 0)`,
              transition: "transform 0.5s cubic-bezier(.22,.7,.2,1)",
              willChange: "transform",
            }}
          >
            <div
              className="wind-ambient"
              style={{
                transformOrigin: "bottom center",
                animationDelay: `${p.phase}s`,
                animationDuration: `${8 + (i % 4) * 0.7}s`,
                transform: `rotate(${angle}deg) ${p.flip ? "scaleX(-1)" : ""}`,
                transition: "transform 0.55s cubic-bezier(.22,.7,.2,1)",
              }}
            >
              <img
                src={PALMS.sketch}
                alt=""
                draggable="false"
                className="block w-full h-auto select-none"
                style={{
                  filter: "url(#elio-white-key)",
                  opacity: 0.42,
                  mixBlendMode: "multiply",
                  userSelect: "none",
                  WebkitUserDrag: "none",
                }}
              />
            </div>
          </div>
        );
      })}
    </>
  );
};

export default BackgroundPalms;
